import React, { useState, useRef } from 'react';
import { computeInitialModality } from './commitmentSheetModality';
import './CommitmentSheet.css';

/**
 * CommitmentSheet — J4, the "before you start" bottom sheet (#1921).
 *
 * Lands over the dimmed J3 shortlist (container renders it as an overlay;
 * this component is the sheet content only — no scrim/portal logic here).
 *
 * DEVIATION FROM THE LITERAL MOCKUP, DELIBERATE AND SCOPED: the Journey
 * Stitch mockup's row 1 ("Choose how to chat — type or voice") taps through
 * to J5 ("Mode select (kept)") to make the actual type/voice choice. J5 is
 * explicitly out of scope here (issue #1921 scope note: "the chooser itself
 * is #1922... do not build" — same "not this issue's screen" posture
 * applies to J5, an A→B segment SEAM neither #1903 nor #1921 owns) — and
 * the live app has no standalone mode-select ROUTE to hand off to any more
 * (`ScenarioDetailPage.jsx`'s own comments: "#1837 removed the
 * briefing/mode-select gates... this landing card IS the one gate").
 * So row 1 here IS the type/voice decision — an inline toggle, not a
 * link to another screen — which is exactly what J1's own promise already
 * commits to ("Type or voice — you choose right before you start").
 * Voice is the DEFAULT nudge (gate decision): the toggle starts on 'voice'.
 * Starting the run reuses the same guest-run-creation call
 * (`POST /api/runs`, `loginAsGuest` if needed) HomePageV2's existing
 * `handleScenarioClick` already uses — this is not a new run-creation path.
 *
 * #2089: the modality default respects `scenario.voice_available` — a
 * text_only scenario (allowed_modality) never offers or defaults to voice
 * here, and `initialModality` (set by the #1922 chooser's Q3, when the
 * guest arrived via the chooser) is honoured only when the scenario
 * actually supports it. `computeInitialModality` is the pure, unit-tested
 * decision (`commitmentSheetModality.js`) — kept out of this component so
 * it's testable without a jsdom render.
 *
 * Fix round 2026-08-24 (Gilbert, ephemeral human-test): step 2 ("Read one
 * short brief") used to be a LIE for the primary CTA — `onStart` skipped
 * the briefing screen entirely (the #2097-regression this same round's
 * ChatPage fix corrects). Now that ChatPage genuinely shows the brief
 * before chat whenever a mode preference is set, the three numbered steps
 * are all truthful for the one remaining path — so the separate "Read the
 * brief" secondary button is REMOVED as redundant, not replaced. Toggle
 * labels shortened to "Type"/"Speak" (dropping "Type it"/"Say it out
 * loud"); "Something else" renamed "Pick a different scenario" (identical
 * `onDismiss` behaviour, just literal about what it does).
 *
 * Second copy trim, same 2026-08-24 re-walk: the AI-role name is dropped
 * from step 3's title ("Start talking" — was "Start talking to {aiRole}")
 * and the primary CTA ("Start talking"/"Start typing" — was "…with
 * {aiRole}"). This was a LENGTH note, not a request to drop the mode
 * signal — the CTA still branches on `modality` (talking vs typing); only
 * the trailing name is gone, so `scenario.ai_role` has no remaining reader
 * in this component.
 */
function CommitmentSheet({ scenario, isStarting, onDismiss, onStart, initialModality }) {
  const voiceAvailable = scenario?.voice_available !== false;
  const [modality, setModality] = useState(() => computeInitialModality(scenario, initialModality));
  // The sheet is a single long-lived component instance re-used across
  // different `scenario` selections (CommitmentSheetOverlay never remounts
  // it) — without this, `useState`'s lazy initializer only ever runs once
  // and a later text_only scenario would silently inherit an earlier
  // scenario's 'voice' selection.
  const lastScenarioIdRef = useRef(scenario?.id);
  if (scenario?.id !== lastScenarioIdRef.current) {
    lastScenarioIdRef.current = scenario?.id;
    const nextModality = computeInitialModality(scenario, initialModality);
    if (nextModality !== modality) setModality(nextModality);
  }

  if (!scenario) return null;

  return (
    <div className="gd-commitment-sheet" data-testid="gd-commitment-sheet" role="dialog" aria-modal="true">
      <div className="gd-commitment-sheet__handle" aria-hidden="true" />
      <p className="gd-commitment-sheet__eyebrow">Before you start</p>
      <h2 className="gd-commitment-sheet__title">{scenario.name}</h2>

      <ol className="gd-commitment-sheet__steps">
        <li className="gd-commitment-sheet__step">
          <span className="gd-commitment-sheet__step-num">1</span>
          <div className="gd-commitment-sheet__step-body">
            <p className="gd-commitment-sheet__step-title">
              {voiceAvailable ? 'Choose how to chat — type or voice' : 'This one is type-only'}
            </p>
            <div className="gd-modality-toggle" role="group" aria-label="Choose how to chat">
              <button
                type="button"
                className={`gd-modality-toggle__option${modality === 'text' ? ' gd-modality-toggle__option--active' : ''}`}
                onClick={() => setModality('text')}
                aria-pressed={modality === 'text'}
              >
                <span aria-hidden="true">⌨️</span> Type
              </button>
              {/* #2089: text_only scenarios never even offer voice here —
                  a sheet-level gate on top of ChatPage's own guard
                  (which clears a preselected voice mode once run.allowed_modality
                  resolves to text_only; this stops the toggle from ever
                  claiming voice works on the 2 text-only scenarios in the
                  first place). */}
              {voiceAvailable && (
                <button
                  type="button"
                  className={`gd-modality-toggle__option${modality === 'voice' ? ' gd-modality-toggle__option--active' : ''}`}
                  onClick={() => setModality('voice')}
                  aria-pressed={modality === 'voice'}
                >
                  <span aria-hidden="true">🎙</span> Speak
                </button>
              )}
            </div>
          </div>
        </li>
        <li className="gd-commitment-sheet__step">
          <span className="gd-commitment-sheet__step-num">2</span>
          <div className="gd-commitment-sheet__step-body">
            <p className="gd-commitment-sheet__step-title">Read one short brief</p>
            <p className="gd-commitment-sheet__step-detail">You can reopen it mid-conversation.</p>
          </div>
        </li>
        <li className="gd-commitment-sheet__step">
          <span className="gd-commitment-sheet__step-num">3</span>
          <div className="gd-commitment-sheet__step-body">
            <p className="gd-commitment-sheet__step-title">Start talking</p>
            <p className="gd-commitment-sheet__step-detail">Leaving early is fine — you still get your feedback.</p>
          </div>
        </li>
      </ol>

      <div className="gd-commitment-sheet__privacy">
        <span aria-hidden="true">🔒</span>
        <span>Guest session — no account needed. We keep the conversation so we can write your feedback from it.</span>
      </div>

      <button
        type="button"
        className="gd-commitment-sheet__primary"
        onClick={() => onStart(modality)}
        disabled={isStarting}
        data-testid="gd-commitment-sheet-start"
      >
        {isStarting ? 'Starting…' : `Start ${modality === 'voice' ? 'talking' : 'typing'}`}
      </button>
      <div className="gd-commitment-sheet__secondary-row">
        <button type="button" className="gd-commitment-sheet__secondary" onClick={onDismiss}>
          Pick a different scenario
        </button>
      </div>
    </div>
  );
}

export default CommitmentSheet;
